import { cn } from "@gymflow/helpers";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";

import { Popover, PopoverContent, PopoverTrigger } from "../../atoms/Popover";
import { CalendarWithNumberIcon } from "../icons";
import DatePickerNew from "./DatePickerNew/DatePickerNew";

type DatePickerPopoverProps = {
  label?: string;
  range?: DateRange;
  applyRange: (range: DateRange) => void;
  isDisabled?: boolean;
};

export const DatePickerPopover = ({
  label,
  range,
  applyRange,
  isDisabled = false,
}: DatePickerPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localRange, setLocalRange] = useState<DateRange | undefined>(range);
  const [displayMonth, setDisplayMonth] = useState<Date | undefined>();

  const { t } = useTranslation();

  useEffect(() => {
    if (range && range?.from && range?.to) {
      setLocalRange(range);
      setDisplayMonth(range.from);
    }
  }, [range]);

  const handleCancel = () => setIsOpen(false);

  const handleApply = () => {
    if (localRange && localRange.from && localRange.to) {
      applyRange(localRange);
      setDisplayMonth(localRange.from);
    }

    setIsOpen(false);
  };

  const formatRangeLabel = () => {
    if (!label) return t("page.userWallets.filter.selectRange");
    if (!range?.from || !range?.to) return label;
    const from = range.from.toLocaleDateString(undefined, DateTime.DATE_MED);
    const to = range.to.toLocaleDateString(undefined, DateTime.DATE_MED);
    return `${from} - ${to}`;
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        disabled={isDisabled}
        className={cn(
          "flex h-10 items-center justify-between md:w-[23rem] rounded-lg border border-gray-200 bg-white px-3.5 text-sm shadow-xs hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-700 dark:hover:!bg-gray-800 dark:focus:!bg-gray-800",
          {
            "cursor-not-allowed bg-gray-100 text-gray-400": isDisabled,
          },
        )}
      >
        <div
          className={cn(
            "text-gray-500 font-medium line-clamp-1 dark:text-gray-0 dark:focus:!text-gray-500 dark:hover:text-gray-500",
            {
              "text-gray-950": isOpen,
            },
          )}
        >
          {formatRangeLabel()}
        </div>
        <div className="flex flex-row items-center gap-x-2">
          <CalendarWithNumberIcon
            pathClassName={cn("stroke-gray-500", {
              "stroke-secondary-500": isOpen,
            })}
          />
        </div>
      </PopoverTrigger>

      <PopoverContent className="p-0">
        <DatePickerNew
          range={localRange}
          onSelect={(selected) =>
            setLocalRange(selected ?? { from: undefined, to: undefined })
          }
          onApply={handleApply}
          onCancel={handleCancel}
          applyRange={handleApply}
          displayMonth={displayMonth}
          onMonthChange={setDisplayMonth}
        />
      </PopoverContent>
    </Popover>
  );
};

export default DatePickerPopover;
